import { useEffect, useState } from 'react';

/**
 * Custom hook to handle iOS Safari viewport height issues
 * Fixes the problem where 100vh doesn't account for dynamic browser UI
 * during pull-to-refresh and other Safari behaviors
 */
export const useViewportHeight = () => {
  const [viewportHeight, setViewportHeight] = useState('100vh');

  useEffect(() => {
    const setVH = () => {
      // Get the actual viewport height
      const vh = window.innerHeight * 0.01;
      // Set CSS custom property
      document.documentElement.style.setProperty('--vh', `${vh}px`);
      setViewportHeight(`${vh}px`);
    };

    // Set initial value
    setVH();

    // Update on resize and orientation change
    const handleResize = () => {
      // Use requestAnimationFrame to ensure the resize is complete
      requestAnimationFrame(setVH);
    };

    // Listen for various events that might change viewport
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    
    // iOS Safari specific events
    window.addEventListener('scroll', handleResize, { passive: true });
    
    // Handle visual viewport changes (iOS Safari)
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleResize);
    }

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
      window.removeEventListener('scroll', handleResize);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleResize);
      }
    };
  }, []);

  return viewportHeight;
};

/**
 * Utility function to get the current viewport height in pixels
 * Can be used outside of React components
 */
export const getViewportHeight = () => {
  return window.innerHeight;
};

/**
 * Utility function to set the viewport height CSS custom property
 * Can be used outside of React components
 */
export const setViewportHeight = () => {
  const vh = window.innerHeight * 0.01;
  document.documentElement.style.setProperty('--vh', `${vh}px`);
  return vh;
};
